import { loadConnectAndInitialize, StripeConnectInstance } from "@stripe/connect-js";
import { ConnectComponentsProvider, ConnectPaymentDisputes } from "@stripe/react-connect-js";
import { Alert, Drawer, flash } from "@xola/ui-kit";
import { useMount, useUnmount } from "ahooks";
import React, { useRef, useState } from "react";
import { api } from "@/api/api";
import { Loader } from "@/components/Loader";
import { config } from "@/config";
import { getSeller } from "@/utils/getSeller";

interface StripeSessionResponse {
    client_secret?: string;
    clientSecret?: string;
}

interface DisputeChallengeDrawerProps {
    isOpen: boolean;
    paymentId?: string;
    onClose: () => void;
}

export const DisputeChallengeDrawer = ({ isOpen, paymentId, onClose }: DisputeChallengeDrawerProps) => {
    return (
        <Drawer
            size="large"
            isOpen={isOpen}
            content={paymentId ? <DrawerContent paymentId={paymentId} /> : null}
            classNames={{ dialog: "z-20" }}
            onClose={onClose}
        />
    );
};

const DrawerContent = ({ paymentId }: { paymentId: string }) => {
    const [stripeConnectInstance, setStripeConnectInstance] = useState<StripeConnectInstance>();
    const [isStripeReady, setIsStripeReady] = useState(false);
    const [error, setError] = useState<string>();
    const seller = getSeller();
    const isMountedRef = useRef(true);

    const publicKey = seller.defaultGateway?.publicKey ?? config.stripe.publicKey;

    const fetchStripeSession = async (): Promise<string> => {
        const response = await api.post<StripeSessionResponse>(`/sellers/${seller.id}/stripeAccount/sessions`, {
            components: { payment_disputes: { enabled: true } },
        });

        const clientSecret = response.data.client_secret ?? response.data.clientSecret;

        if (!clientSecret) {
            throw new Error("Invalid session response - no client_secret");
        }

        return clientSecret;
    };

    useMount(() => {
        if (!publicKey) {
            setError("Stripe is not configured for this account.");
            return;
        }

        try {
            const instance = loadConnectAndInitialize({
                publishableKey: publicKey,
                fetchClientSecret: fetchStripeSession,
                appearance: {
                    overlays: "dialog",
                    variables: {
                        colorPrimary: "#1352C6",
                        colorBackground: "#ffffff",
                        colorText: "#222324",
                        colorDanger: "#FF5A5A",
                        colorSecondaryText: "#505254",
                        colorBorder: "#E0E3E7",
                        fontFamily: "Inter, system-ui, sans-serif",
                        fontSizeBase: "16px",
                        borderRadius: "4px",
                        spacingUnit: "4px",
                    },
                },
            });

            if (isMountedRef.current) {
                setStripeConnectInstance(instance);
            }
        } catch (error_) {
            console.error("Failed to initialize Stripe Connect:", error_);
            if (isMountedRef.current) {
                flash.danger("Failed to initialize Stripe. Please try again.");
                setError("Failed to initialize Stripe. Please try again.");
            }
        }
    });

    useUnmount(() => {
        isMountedRef.current = false;
    });

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center">
                <Alert color="danger">{error}</Alert>
            </div>
        );
    }

    return (
        <>
            {!isStripeReady && <Loader className="py-20" text="Loading dispute..." />}
            {stripeConnectInstance && (
                <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                    <div className="min-h-[400px] rounded-lg bg-white p-4">
                        <ConnectPaymentDisputes payment={paymentId} onDisputesLoaded={() => setIsStripeReady(true)} />
                    </div>
                </ConnectComponentsProvider>
            )}
        </>
    );
};
